import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import FoundersListForm from '@/components/sections/FoundersListForm';
import SectionMarker from '@/components/sections/SectionMarker';
import { usePageMeta } from '@/lib/usePageMeta';
import { PHOTOS } from '@/constants/content';

const RECEIVE_ITEMS = [
  'What I built this week',
  'AI tools and workflows I’m using',
  'Business lessons from the build',
  'Technology and platforms I’m testing',
  'Companies I’m developing',
  'Opportunities I’m watching',
  'Early access to launches, resources, and announcements',
];

const AUDIENCE = [
  'Founders',
  'Entrepreneurs',
  'Technology builders',
  'Business owners',
  'Investors',
  'Visionaries',
  'People interested in AI, SaaS, international business, and global opportunities',
  'Anyone who wants an honest behind-the-scenes look at building companies in real time',
];

export default function FoundersList() {
  usePageMeta({
    title: 'The Founder’s List | Toia Wallace',
    description:
      'Join The Founder’s List and receive The Founder’s Brief every Friday, featuring behind-the-scenes insights on technology, AI, business, platforms, and global ventures.',
    path: '/founders-list',
    image: PHOTOS.editorial,
    ogTitle: 'Join The Founder’s List | Toia Wallace',
    ogDescription:
      'Receive The Founder’s Brief every Friday, featuring a behind-the-scenes look at technology, AI, business, platforms, and global ventures.',
  });

  return (
    <div className="bg-ivory" data-testid="founders-list-page">
      {/* HERO */}
      <section className="pt-40 lg:pt-48 pb-20 lg:pb-28">
        <div className="mx-auto max-w-[1240px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-6">
            <div className="eyebrow text-taupe mb-6">A behind-the-scenes founder journal</div>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tightest text-matte">
              Join The
              <br />
              <em className="italic text-taupe">Founder’s List.</em>
            </h1>
            <p className="mt-8 font-serif-display italic text-2xl md:text-3xl leading-snug text-taupe max-w-lg">
              Get a five-minute look inside what I’m building, what I’m learning, and what’s coming next.
            </p>
            <div className="mt-10 space-y-6 text-matte/75 leading-[1.85] max-w-xl">
              <p>Building companies isn’t just about the destination. It’s about the journey.</p>
              <p>
                The Founder’s List gives you behind-the-scenes access as I build technology companies, AI platforms,
                global ventures, and opportunities designed to create meaningful impact.
              </p>
              <p>Every Friday, you’ll receive The Founder’s Brief directly from my desk to your inbox.</p>
            </div>
          </div>

          {/* Form panel */}
          <div className="lg:col-span-6 lg:sticky lg:top-32">
            <div className="bg-white border border-taupe/30 rounded-3xl p-8 md:p-12 luxury-shadow">
              <div className="eyebrow text-taupe mb-3">From my desk to yours</div>
              <div className="font-serif-display text-3xl md:text-4xl leading-tight tracking-tight text-matte mb-8">
                Be the first to know what I’m building next.
              </div>
              <FoundersListForm source="founders_list_page" variant="panel" redirectOnSuccess />
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU'LL RECEIVE */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="mx-auto max-w-[1240px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <SectionMarker number="01" label="What You’ll Receive" />
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.98] text-matte">
              Inside The Founder’s Brief.
            </h2>
            <ul className="mt-12 divide-y divide-taupe/20 border-y border-taupe/20" data-testid="fl-receive-list">
              {RECEIVE_ITEMS.map((item, i) => (
                <li key={i} className="flex items-baseline gap-6 py-5">
                  <span className="font-label text-[10px] tracking-widest uppercase text-taupe w-10 shrink-0">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-[17px] text-matte/85 leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-10 font-serif-display italic text-2xl text-taupe leading-snug">
              No fluff. No spam. Just one thoughtful email every Friday.
            </p>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-24 lg:py-32 bg-matte text-ivory relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 noise-grain pointer-events-none" aria-hidden />
        <div className="relative mx-auto max-w-[1240px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <div className="eyebrow text-gold mb-6">Who It’s For</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight tracking-tight">
              For the ones <em className="italic text-gold">building next.</em>
            </h2>
          </div>
          <div className="lg:col-span-8">
            <p className="text-ivory/75 leading-[1.85] mb-10 max-w-2xl">
              The Founder’s List is for founders, entrepreneurs, and visionaries who want a real look inside what it
              takes to build companies, platforms, and global opportunities in real time.
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {AUDIENCE.map((a, i) => (
                <li key={i} className="flex items-baseline gap-3 text-ivory/85 leading-relaxed">
                  <span className="text-gold text-[11px]">○</span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FOUNDER SYSTEMS SCORECARD CTA */}
      <section className="py-20 lg:py-28 bg-white border-t border-taupe/20" data-testid="fl-scorecard-cta">
        <div className="mx-auto max-w-[1100px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow text-taupe mb-4">Founder Systems Scorecard<span className="align-super text-[9px]">™</span></div>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-matte">
              Before Friday&rsquo;s brief &mdash; discover <em className="italic text-taupe">what to build next.</em>
            </h2>
            <p className="mt-5 text-matte/70 max-w-xl leading-relaxed">
              A 7-minute assessment across seven categories that affect your ability to grow without creating more chaos.
            </p>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <Link
              to="/founder-scorecard"
              data-testid="fl-scorecard-cta-btn"
              className="inline-flex items-center gap-3 bg-matte text-ivory px-7 py-4 rounded-full text-[11px] tracking-[0.22em] uppercase hover:bg-gold hover:text-matte transition-all"
            >
              Take the Free Scorecard
              <ArrowRight size={14} strokeWidth={1.6} />
            </Link>
            <div className="mt-4 text-[11px] tracking-[0.18em] uppercase text-matte/50">28 Questions · Immediate Results</div>
          </div>
        </div>
      </section>

      {/* COMMUNITY LINE + FINAL CTA */}
      <section className="py-28 lg:py-40 bg-ivory">
        <div className="mx-auto max-w-[820px] px-6 sm:px-10 text-center">
          <div className="eyebrow text-taupe mb-8">Final Word</div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-matte">
            Join founders, entrepreneurs, and visionaries <em className="italic text-taupe">building what’s next.</em>
          </h2>
          <p className="mt-8 text-matte/70 leading-relaxed max-w-lg mx-auto">
            Be the first to know what I’m building next. Every Friday, five minutes, direct from my desk to yours.
          </p>
          <div className="mt-12">
            <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="btn-primary" data-testid="fl-final-cta">
              Join The Founder’s List
              <ArrowRight size={14} strokeWidth={1.6} />
            </a>
          </div>
          <div className="mt-10">
            <Link to="/journal" className="btn-ghost">
              Read the Founder Journal
              <ArrowRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
